/**
 * Движок синхронизации: первичное наполнение, replay, live-режим,
 * polling-fallback и полная пересинхронизация.
 *
 * CC §4.3 — первый запуск и вход; §4.5 — WebSocket; §4.8 — reset;
 * §3.3 — состояния синхронизации.
 */
import { api, unwrap } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type { Contact, Conversation, Match, Me, SyncEvent } from '@shared/api/types'
import {
  blocksRepo,
  contactsRepo,
  conversationRefreshRepo,
  conversationsRepo,
  matchesRepo,
  meRepo,
  messagesRepo,
  notificationsRepo,
  openUserDb,
  queueRepo,
  syncStateRepo,
} from '@shared/db'
import { dismissPairNotifications } from '@shared/push/dismiss'
import { combineAbortSignals } from '@shared/lib/abort'
import {
  SyncSocket,
  type ByeReason,
  type PresenceFrame,
  type SocketStatus,
  type TypingFrame,
} from '@shared/ws/socket'
import {
  isLeaderTab,
  hasExclusiveLeadership,
  onLeadershipChange,
  onTabMessage,
  postTabMessage,
  releaseLeadership,
  startLeaderElection,
  TAB_ID,
  type TabMessage,
} from '@shared/ws/leader'

import { applyEvent, type ApplyEffects } from './apply'
import {
  setQueueContext,
  setReplayComplete,
  runQueueAfterResetLists,
  startQueueRunner,
  stopQueueRunnerAndRelease,
  triggerQueue,
} from './queue-runner'

/** Итог обработки медиа на сервере. */
export interface MediaEvent {
  mediaId: string
  state: 'ready' | 'rejected'
  rejectedReason?: string
}

/** CC §3.3 — состояния синхронизации. */
export type SyncStatus =
  | 'idle'
  | 'initial_snapshot'
  | 'replaying'
  | 'live'
  | 'polling_fallback'
  | 'reset_required'
  | 'offline'

/** Интервал опроса, когда WebSocket недоступен (CC §4.5 п. 7). */
const POLLING_INTERVAL_MS = 30_000
/** Защита запуска/reset от бесконечной цепочки повреждённых cursor. */
const MAX_LIST_PAGES = 1000
/** Remote active/visible не живут дольше серверного TTL active_chat. */
const REMOTE_HINT_TTL_MS = 60_000
const REMOTE_HINT_SWEEP_MS = 10_000
const LEADERSHIP_RETRY_MS = 1_000

interface EngineRunContext {
  userId: string
  generation: number
  signal: AbortSignal
}

interface CursorPage<T> {
  items: T[]
  next_cursor: string | null
}

const ENGINE_CONTEXT_CHANGED = Symbol('sync-engine-context-changed')

export interface EngineCallbacks {
  onStatusChange?: (status: SyncStatus) => void
  onEffects?: (effects: ApplyEffects) => void
  onTyping?: (frame: TypingFrame) => void
  onPresence?: (frame: PresenceFrame) => void
  onSessionLost?: () => void
  onConsentRequired?: () => void
  onClientUpgradeRequired?: () => void
  onForbidden?: () => void
  onBye?: (reason: ByeReason) => void
  onNewMatch?: (match: Match) => void
}

export class SyncEngine {
  private socket: SyncSocket | null = null
  private status: SyncStatus = 'idle'
  private userId: string | null = null
  private pollingTimer: ReturnType<typeof setInterval> | null = null
  private pollingGeneration = 0
  private pollingRequest: Promise<void> | null = null
  private pollingController: AbortController | null = null
  private remoteHintTimer: ReturnType<typeof setInterval> | null = null
  private activeConversationId: string | null = null
  private activeConversationAt = 0
  private visibleConversations: string[] = []
  private readonly remoteActiveConversations = new Map<
    string,
    { conversationId: string | null; at: number }
  >()
  private readonly remoteVisibleConversations = new Map<string, { ids: string[]; at: number }>()
  private wsFailures = 0
  private lifecycleGeneration = 0
  private lifecycleController: AbortController | null = null
  private sessionUserId: string | null = null
  private sessionGeneration = 0
  private leadershipRevision = 0
  private leadershipTransitions: Promise<void> = Promise.resolve()
  private leadershipUnsubscribe: (() => void) | null = null
  private tabUnsubscribe: (() => void) | null = null
  private leaderCleanup: Promise<void> = Promise.resolve()
  private leadershipRetryTimer: ReturnType<typeof setTimeout> | null = null
  private conversationRefreshTimer: ReturnType<typeof setTimeout> | null = null
  private lastLeaderEpoch: string | null = null

  /*
   * Эфемерные кадры раздаются подписчикам напрямую: typing и presence
   * не durable (BS §2.3), в реплику не пишутся и живут только пока открыт
   * соответствующий экран.
   */
  private readonly typingListeners = new Set<(frame: TypingFrame) => void>()
  private readonly presenceListeners = new Set<(frame: PresenceFrame) => void>()

  /**
   * Результат обработки медиа. В статусе `active` готовность приходит
   * событием `media.ready` / `media.rejected` и опрос НЕ требуется (CC §8.6),
   * поэтому загрузчику нужен способ дождаться именно события.
   */
  private readonly mediaListeners = new Set<(result: MediaEvent) => void>()

  /**
   * Недавние результаты обработки медиа.
   *
   * Событие `media.ready` может прийти РАНЬШЕ, чем загрузчик успеет
   * подписаться (обработка бывает быстрее круга промисов). Без этой памяти
   * подписчик ждал бы событие, которого уже не будет.
   */
  private readonly recentMedia = new Map<string, { result: MediaEvent; at: number }>()

  constructor(private readonly callbacks: EngineCallbacks = {}) {}

  /** Подписка экрана чата на индикатор набора собеседника. */
  onTypingFrame(listener: (frame: TypingFrame) => void): () => void {
    this.typingListeners.add(listener)
    return () => this.typingListeners.delete(listener)
  }

  /** Подписка на presence контрагентов видимых диалогов. */
  onPresenceFrame(listener: (frame: PresenceFrame) => void): () => void {
    this.presenceListeners.add(listener)
    return () => this.presenceListeners.delete(listener)
  }

  /** Подписка на готовность или отклонение медиа (CC §8.6). */
  onMediaEvent(listener: (result: MediaEvent) => void): () => void {
    this.mediaListeners.add(listener)
    return () => this.mediaListeners.delete(listener)
  }

  /** Результат, если он уже приходил, — закрывает гонку подписки. */
  takeMediaResult(mediaId: string): MediaEvent | null {
    const entry = this.recentMedia.get(mediaId)
    if (!entry) return null

    this.recentMedia.delete(mediaId)
    return entry.result
  }

  getStatus(): SyncStatus {
    return this.status
  }

  /** Только leader получает sync-события; follower использует безопасные fallback-пути. */
  isNetworkLeader(): boolean {
    return isLeaderTab() && this.currentContext() !== null
  }

  private setStatus(status: SyncStatus, broadcast = true): void {
    if (this.status === status) return
    this.status = status
    this.callbacks.onStatusChange?.(status)
    if (broadcast && isLeaderTab() && this.sessionUserId) {
      postTabMessage({ type: 'sync_status', user_id: this.sessionUserId, status })
    }
  }

  private beginLifecycle(userId: string): EngineRunContext {
    this.lifecycleController?.abort()
    this.lifecycleGeneration += 1
    const controller = new AbortController()
    this.lifecycleController = controller
    this.userId = userId
    return { userId, generation: this.lifecycleGeneration, signal: controller.signal }
  }

  private currentContext(): EngineRunContext | null {
    if (!this.userId || !this.lifecycleController) return null
    return {
      userId: this.userId,
      generation: this.lifecycleGeneration,
      signal: this.lifecycleController.signal,
    }
  }

  private isCurrentContext(context: EngineRunContext): boolean {
    return (
      !context.signal.aborted &&
      isLeaderTab() &&
      this.sessionUserId === context.userId &&
      this.userId === context.userId &&
      this.lifecycleGeneration === context.generation &&
      this.lifecycleController?.signal === context.signal
    )
  }

  private assertCurrentContext(context: EngineRunContext): void {
    if (!this.isCurrentContext(context)) throw ENGINE_CONTEXT_CHANGED
  }

  private assertSyncSource(context: EngineRunContext, sourceSignal?: AbortSignal): void {
    this.assertCurrentContext(context)
    if (sourceSignal?.aborted) throw ENGINE_CONTEXT_CHANGED
  }

  private syncSourceSignal(context: EngineRunContext, sourceSignal?: AbortSignal): AbortSignal {
    return sourceSignal ? combineAbortSignals(context.signal, sourceSignal) : context.signal
  }

  private isStaleFailure(error: unknown, context: EngineRunContext): boolean {
    return error === ENGINE_CONTEXT_CHANGED || !this.isCurrentContext(context)
  }

  private requestQueueRun(context: EngineRunContext): void {
    if (!this.isCurrentContext(context)) return
    triggerQueue()
  }

  /* ===================================================================== */
  /* Запуск                                                                */
  /* ===================================================================== */

  /**
   * CC §4.3. Первый запуск = snapshot. При обычном запуске шаги 4–5
   * пропускаются: cursor берётся из `sync_state`.
   */
  async start(userId: string, initialMe?: Me): Promise<void> {
    const sessionGeneration = ++this.sessionGeneration
    this.sessionUserId = userId
    openUserDb(userId)
    const initialState = await syncStateRepo.init(userId)
    if (!this.isCurrentSession(userId, sessionGeneration)) return

    // Свежий `GET /me` со стартовой последовательности: `user.device_id` текущей
    // сессии нужен кадру hello и при обычном запуске (без snapshot) взять его больше неоткуда.
    if (initialMe) await meRepo.put(initialMe)
    if (!this.isCurrentSession(userId, sessionGeneration)) return

    this.setStatus(this.statusFromReplica(initialState.status), false)
    this.tabUnsubscribe?.()
    this.tabUnsubscribe = onTabMessage((message) => {
      if ('user_id' in message && message.user_id !== userId) return
      if (message.type === 'sync_status' && !isLeaderTab()) {
        this.setStatus(message.status, false)
      }
      if (message.type === 'media_event' && !isLeaderTab()) {
        this.emitMedia(
          {
            mediaId: message.media_id,
            state: message.state,
            ...(message.rejected_reason ? { rejectedReason: message.rejected_reason } : {}),
          },
          false,
        )
      }
      if (message.type === 'sync_effects' && !isLeaderTab()) {
        this.callbacks.onEffects?.({
          ...(message.incoming_message ? { incomingMessage: message.incoming_message } : {}),
          ...(message.match_created ? { matchCreated: message.match_created } : {}),
        })
      }
      if (message.type === 'leader_heartbeat' && !isLeaderTab()) {
        const epoch = `${message.tab_id}:${message.started_at}`
        if (this.lastLeaderEpoch !== epoch) {
          this.lastLeaderEpoch = epoch
          this.publishLocalHintsToLeader(userId)
        }
        void syncStateRepo
          .get(userId)
          .then((state) => {
            if (!state || !this.isCurrentSession(userId, sessionGeneration) || isLeaderTab()) return
            this.setStatus(this.statusFromReplica(state.status), false)
          })
          .catch(() => undefined)
      }
      if (message.type === 'leader_ready' && !isLeaderTab()) {
        this.publishLocalHintsToLeader(userId)
      }
      if (message.type === 'leader_candidate' && isLeaderTab()) {
        postTabMessage({ type: 'sync_status', user_id: userId, status: this.status })
      }
      if (message.type === 'sync_command' && isLeaderTab()) this.handleFollowerCommand(message)
    })

    let initialTransition = Promise.resolve()
    let subscribing = true
    this.leadershipUnsubscribe?.()
    this.leadershipUnsubscribe = onLeadershipChange((leader) => {
      if (leader) this.lastLeaderEpoch = null
      const transition = this.scheduleLeadershipTransition(leader, userId, sessionGeneration)
      if (subscribing) initialTransition = transition
      else {
        void transition.catch((error: unknown) => {
          if (this.isCurrentSession(userId, sessionGeneration)) {
            console.error('sync leader transition failed', error)
          }
        })
      }
    })
    subscribing = false
    await initialTransition
  }

  stop(): void {
    this.sessionGeneration += 1
    this.sessionUserId = null
    this.leadershipRevision += 1
    this.leadershipUnsubscribe?.()
    this.leadershipUnsubscribe = null
    this.tabUnsubscribe?.()
    this.tabUnsubscribe = null
    this.stopLeaderWork()
    if (this.leadershipRetryTimer) clearTimeout(this.leadershipRetryTimer)
    this.leadershipRetryTimer = null
    this.lastLeaderEpoch = null
    this.activeConversationId = null
    this.visibleConversations = []
    this.remoteActiveConversations.clear()
    this.remoteVisibleConversations.clear()
    this.wsFailures = 0
    this.recentMedia.clear()
    this.typingListeners.clear()
    this.presenceListeners.clear()
    this.mediaListeners.clear()
    this.setStatus('idle')
  }

  private isCurrentSession(userId: string, generation: number): boolean {
    return this.sessionUserId === userId && this.sessionGeneration === generation
  }

  private handleFollowerCommand(message: Extract<TabMessage, { type: 'sync_command' }>): void {
    if (message.user_id !== this.sessionUserId) return
    const { command } = message
    switch (command.kind) {
      case 'typing':
        this.socket?.sendTyping(command.conversation_id, command.state)
        break
      case 'receipt':
        this.socket?.sendReceipt(command.conversation_id, {
          ...(command.delivered_seq !== undefined ? { delivered_seq: command.delivered_seq } : {}),
          ...(command.read_seq !== undefined ? { read_seq: command.read_seq } : {}),
        })
        break
      case 'active_chat':
        this.remoteActiveConversations.set(message.source_tab_id, {
          conversationId: command.conversation_id,
          at: Date.now(),
        })
        this.publishCombinedActiveConversation()
        break
      case 'visible':
        this.remoteVisibleConversations.set(message.source_tab_id, {
          ids: command.conversation_ids,
          at: Date.now(),
        })
        this.publishCombinedVisibleConversations()
        break
    }
  }

  private publishLocalHintsToLeader(userId: string): void {
    postTabMessage({
      type: 'sync_command',
      user_id: userId,
      source_tab_id: TAB_ID,
      command: { kind: 'active_chat', conversation_id: this.activeConversationId },
    })
    postTabMessage({
      type: 'sync_command',
      user_id: userId,
      source_tab_id: TAB_ID,
      command: { kind: 'visible', conversation_ids: [...this.visibleConversations] },
    })
  }

  private publishCombinedActiveConversation(): void {
    this.socket?.setActiveConversation(this.combinedActiveConversation())
  }

  private combinedActiveConversation(): string | null {
    this.pruneRemoteHints()
    let selected = this.activeConversationId
    let selectedAt = this.activeConversationId ? this.activeConversationAt : 0
    for (const hint of this.remoteActiveConversations.values()) {
      if (hint.conversationId && hint.at >= selectedAt) {
        selected = hint.conversationId
        selectedAt = hint.at
      }
    }
    return selected
  }

  private publishCombinedVisibleConversations(): void {
    this.socket?.setVisibleConversations(this.combinedVisibleConversations())
  }

  private combinedVisibleConversations(): string[] {
    this.pruneRemoteHints()
    const combined = new Set(this.visibleConversations)
    for (const hint of this.remoteVisibleConversations.values()) {
      for (const id of hint.ids) {
        if (combined.size >= 50) break
        combined.add(id)
      }
    }
    return [...combined]
  }

  private pruneRemoteHints(): void {
    const cutoff = Date.now() - REMOTE_HINT_TTL_MS
    for (const [tabId, hint] of this.remoteActiveConversations) {
      if (hint.at <= cutoff) this.remoteActiveConversations.delete(tabId)
    }
    for (const [tabId, hint] of this.remoteVisibleConversations) {
      if (hint.at <= cutoff) this.remoteVisibleConversations.delete(tabId)
    }
  }

  private statusFromReplica(status: Awaited<ReturnType<typeof syncStateRepo.init>>['status']): SyncStatus {
    switch (status) {
      case 'snapshot':
        return 'initial_snapshot'
      case 'polling':
        // Persisted polling означает лишь прежний успех. Новая
        // вкладка подтверждает online только текущим HTTP/WS обменом.
        return 'offline'
      case 'error':
        return 'offline'
      default:
        return status
    }
  }

  private scheduleLeadershipTransition(
    leader: boolean,
    userId: string,
    sessionGeneration: number,
  ): Promise<void> {
    const revision = ++this.leadershipRevision
    // Первая синхронная нотификация подписки для ведомой вкладки не должна
    // затирать восстановленный из общей реплики статус. Останавливать есть что
    // только после фактического запуска лидерской работы.
    if (!leader && (this.lifecycleController || this.socket || this.userId)) this.stopLeaderWork()

    const transition = this.leadershipTransitions
      .catch(() => undefined)
      .then(async () => {
        await this.leaderCleanup
        if (revision !== this.leadershipRevision || !this.isCurrentSession(userId, sessionGeneration)) {
          return
        }
        if (!leader || !isLeaderTab()) return
        await this.startLeaderWork(userId, sessionGeneration)
      })
      .catch((error: unknown) => {
        if (revision !== this.leadershipRevision || !this.isCurrentSession(userId, sessionGeneration)) return
        console.error('sync leader transition failed', error)
        // Неудачный snapshot/list не должен удерживать lock и
        // блокировать здоровую соседнюю вкладку.
        releaseLeadership()
        if (!this.isCurrentSession(userId, sessionGeneration)) return
        this.setStatus('offline', false)
        if (this.leadershipRetryTimer) clearTimeout(this.leadershipRetryTimer)
        this.leadershipRetryTimer = setTimeout(() => {
          this.leadershipRetryTimer = null
          if (this.isCurrentSession(userId, sessionGeneration)) startLeaderElection(userId)
        }, LEADERSHIP_RETRY_MS)
      })
    this.leadershipTransitions = transition
    return transition
  }

  private async startLeaderWork(userId: string, sessionGeneration: number): Promise<void> {
    if (!this.isCurrentSession(userId, sessionGeneration) || !isLeaderTab()) return
    // Remote hints принадлежат конкретной leadership epoch. За время
    // без лидера follower мог скрыться/закрыться, и старый non-null
    // active_chat нельзя переносить в новый WS.
    this.remoteActiveConversations.clear()
    this.remoteVisibleConversations.clear()
    const context = this.beginLifecycle(userId)
    setQueueContext(userId)

    try {
      // Эксклюзивное лидерство уже получено: stale claim упавшей
      // вкладки больше не может быть активным и не должен блокировать
      // outbox до TTL.
      if (hasExclusiveLeadership()) await queueRepo.releaseAllClaims()
      this.assertCurrentContext(context)
      const state = await syncStateRepo.init(userId)
      this.assertCurrentContext(context)
      if (state.cursor === null) {
        await this.fullResync(context, 'initial')
      } else {
        await syncStateRepo.patch(userId, { status: 'replaying' })
        this.assertCurrentContext(context)
        this.setStatus('replaying')
      }
      this.assertCurrentContext(context)

      await this.refreshPendingConversations(context)
      this.assertCurrentContext(context)

      this.connectSocket(context)
      this.assertCurrentContext(context)
      // Socket уже существует, а buildHello читает combined remote hints.
      // Поэтому немедленный ответ follower не потеряется ни до onopen,
      // ни при последующей сборке hello.
      postTabMessage({ type: 'leader_ready', user_id: userId, tab_id: TAB_ID })
      startQueueRunner()
      if (this.leadershipRetryTimer) clearTimeout(this.leadershipRetryTimer)
      this.leadershipRetryTimer = null
      this.remoteHintTimer = setInterval(() => {
        if (!this.isCurrentContext(context)) return
        this.publishCombinedActiveConversation()
        this.publishCombinedVisibleConversations()
      }, REMOTE_HINT_SWEEP_MS)
    } catch (error) {
      if (this.isStaleFailure(error, context)) return
      throw error
    }
  }

  private stopLeaderWork(): void {
    this.lifecycleGeneration += 1
    this.lifecycleController?.abort()
    this.lifecycleController = null
    this.userId = null
    this.socket?.stop()
    this.socket = null
    this.stopPolling()
    if (this.remoteHintTimer) clearInterval(this.remoteHintTimer)
    this.remoteHintTimer = null
    if (this.conversationRefreshTimer) clearTimeout(this.conversationRefreshTimer)
    this.conversationRefreshTimer = null
    this.wsFailures = 0
    this.setStatus('idle')

    const cleanup = stopQueueRunnerAndRelease().catch(() => undefined)
    this.leaderCleanup = cleanup
  }

  /* ===================================================================== */
  /* Полная пересинхронизация (CC §4.8)                                     */
  /* ===================================================================== */

  async fullResync(
    activeContext?: EngineRunContext,
    mode: 'initial' | 'reset' = 'reset',
    sourceSignal?: AbortSignal,
  ): Promise<void> {
    const context = activeContext ?? this.currentContext()
    if (!context) return
    const signal = this.syncSourceSignal(context, sourceSignal)

    try {
      this.assertSyncSource(context, sourceSignal)
      this.setStatus('initial_snapshot')
      setReplayComplete(false)
      await syncStateRepo.patch(context.userId, { status: 'snapshot' })
      this.assertSyncSource(context, sourceSignal)

      // Шаг 1: snapshot. Cursor НЕ сохраняется до шага 6.
      const snapshot = unwrap(await api.GET('/sync/snapshot', { signal }))
      this.assertSyncSource(context, sourceSignal)
      // `user.device_id` необязателен в схеме Me: если snapshot его не вернул,
      // сохраняется значение текущей сессии из реплики (кадр hello без него невалиден).
      const previous = await meRepo.get(context.userId)
      this.assertSyncSource(context, sourceSignal)
      const deviceId = snapshot.me.user.device_id ?? previous?.user.device_id
      await meRepo.put(
        deviceId ? { ...snapshot.me, user: { ...snapshot.me.user, device_id: deviceId } } : snapshot.me,
      )
      this.assertSyncSource(context, sourceSignal)
      await blocksRepo.replaceAll(snapshot.blocked_user_ids)
      this.assertSyncSource(context, sourceSignal)

      // Шаг 2: перезагрузка списков обычными endpoint'ами.
      await this.reloadLists(context, sourceSignal)
      this.assertSyncSource(context, sourceSignal)

      if (mode === 'reset') {
        // Шаг 3 reset-процедуры: свежие списки уже записаны,
        // поэтому pending можно без replay сверить с актуальными диалогами и
        // повторить. Новый cursor до завершения этого шага сохранять запрещено
        // (CC §4.8). Первый запуск следует §4.3: очередь ждёт replay_complete.
        await runQueueAfterResetLists(context.userId, sourceSignal)
        this.assertSyncSource(context, sourceSignal)
      }

      // Шаг 4: только теперь сохраняем cursor.
      await syncStateRepo.patch(context.userId, {
        cursor: snapshot.cursor,
        snapshot_at: new Date().toISOString(),
        status: 'replaying',
        applied_ids: [],
      })
      this.assertSyncSource(context, sourceSignal)

      this.setStatus('replaying')
    } catch (error) {
      if (sourceSignal?.aborted || this.isStaleFailure(error, context)) return
      throw error
    }
  }

  /**
   * CC §4.3 шаг 5: списки диалогов загружаются ЦЕЛИКОМ (все страницы),
   * это требование клиентского поиска C-02 (решение D-30, GAP-ALL-01).
   */
  private async reloadLists(context: EngineRunContext, sourceSignal?: AbortSignal): Promise<void> {
    this.assertSyncSource(context, sourceSignal)
    const signal = this.syncSourceSignal(context, sourceSignal)
    const [active, archived, matches, notifications, contacts] = await Promise.all([
      this.loadAllConversations('active', context, sourceSignal),
      this.loadAllConversations('archived', context, sourceSignal),
      this.loadAllMatches(context, sourceSignal),
      // CC §4.2: notifications хранят загруженные страницы, а не полный
      // авторитетный набор. На snapshot обновляем первую страницу,
      // не удаляя уже загруженную историю.
      api.GET('/notifications', {
        params: { query: { limit: 50 } as never },
        signal,
      }),
      this.loadAllContacts(context, sourceSignal),
    ])
    this.assertSyncSource(context, sourceSignal)

    // Полные списки snapshot авторитетны: отсутствующие в ответе строки устарели.
    // Messages, drafts и pending_ops лежат отдельно и остаются целыми.
    await conversationsRepo.replaceAll([...active, ...archived])
    this.assertSyncSource(context, sourceSignal)
    await matchesRepo.replaceAll(matches)
    this.assertSyncSource(context, sourceSignal)
    await notificationsRepo.putMany(unwrap(notifications).items)
    this.assertSyncSource(context, sourceSignal)
    await contactsRepo.replaceAll(contacts)
    this.assertSyncSource(context, sourceSignal)
  }

  private async loadAllConversations(
    filter: 'active' | 'archived',
    context: EngineRunContext,
    sourceSignal?: AbortSignal,
  ): Promise<Conversation[]> {
    const items: Conversation[] = []
    let cursor: string | null = null
    let pageCount = 0
    const seen = new Set<string>()

    for (;;) {
      this.assertSyncSource(context, sourceSignal)
      if (pageCount >= MAX_LIST_PAGES) {
        throw new Error(`conversation page limit exceeded: ${filter}`)
      }
      pageCount += 1
      const page: CursorPage<Conversation> = unwrap(
        await api.GET('/conversations', {
          params: {
            query: { filter, limit: 100, ...(cursor ? { cursor } : {}) } as never,
          },
          signal: this.syncSourceSignal(context, sourceSignal),
        }),
      )
      this.assertSyncSource(context, sourceSignal)
      items.push(...page.items)

      if (!page.next_cursor) return items
      if (seen.has(page.next_cursor)) throw new Error(`conversation cursor cycle: ${filter}`)
      seen.add(page.next_cursor)
      cursor = page.next_cursor
    }
  }

  private async loadAllMatches(context: EngineRunContext, sourceSignal?: AbortSignal): Promise<Match[]> {
    const items: Match[] = []
    let cursor: string | null = null
    let pageCount = 0
    const seen = new Set<string>()

    for (;;) {
      this.assertSyncSource(context, sourceSignal)
      if (pageCount >= MAX_LIST_PAGES) throw new Error('match page limit exceeded')
      pageCount += 1
      const page: CursorPage<Match> = unwrap(
        await api.GET('/matches', {
          params: {
            query: { filter: 'all', limit: 100, ...(cursor ? { cursor } : {}) } as never,
          },
          signal: this.syncSourceSignal(context, sourceSignal),
        }),
      )
      this.assertSyncSource(context, sourceSignal)
      items.push(...page.items)

      if (!page.next_cursor) return items
      if (seen.has(page.next_cursor)) throw new Error('match cursor cycle')
      seen.add(page.next_cursor)
      cursor = page.next_cursor
    }
  }

  private async loadAllContacts(context: EngineRunContext, sourceSignal?: AbortSignal): Promise<Contact[]> {
    const items: Contact[] = []
    let cursor: string | null = null
    let pageCount = 0
    const seen = new Set<string>()

    for (;;) {
      this.assertSyncSource(context, sourceSignal)
      if (pageCount >= MAX_LIST_PAGES) throw new Error('contact page limit exceeded')
      pageCount += 1
      const page: CursorPage<Contact> = unwrap(
        await api.GET('/contacts', {
          params: { query: { limit: 100, ...(cursor ? { cursor } : {}) } as never },
          signal: this.syncSourceSignal(context, sourceSignal),
        }),
      )
      this.assertSyncSource(context, sourceSignal)
      items.push(...page.items)

      if (!page.next_cursor) return items
      if (seen.has(page.next_cursor)) throw new Error('contact cursor cycle')
      seen.add(page.next_cursor)
      cursor = page.next_cursor
    }
  }

  /* ===================================================================== */
  /* WebSocket                                                             */
  /* ===================================================================== */

  private connectSocket(context: EngineRunContext): void {
    this.assertCurrentContext(context)
    if (this.socket) return

    let durableConnectionGeneration = -1
    let durableSourceFence: Promise<void> = Promise.resolve()
    const fenceDurableSource = (connectionGeneration: number): Promise<void> => {
      if (durableConnectionGeneration === connectionGeneration) return durableSourceFence
      durableConnectionGeneration = connectionGeneration
      // С первого durable-кадра outbox снова закрыт: WS может
      // ещё replay-ить пропущенные события или потребовать reset.
      setReplayComplete(false)
      this.setStatus('replaying')
      durableSourceFence = this.stopPollingAndWait().then(() => this.assertCurrentContext(context))
      return durableSourceFence
    }

    this.socket = new SyncSocket(
      {
        onEvent: async (event, connectionGeneration) => {
          // Replay-event может прийти до ready. Первый durable-кадр
          // каждого WS-соединения сначала отменяет и дожидается
          // polling/reset-источника, чтобы replaceAll не затёр свежее WS-событие.
          await fenceDurableSource(connectionGeneration)
          await this.handleEvent(event, context)
        },

        onReady: async (frame, connectionGeneration) => {
          if (!this.isCurrentContext(context)) return
          // Любой durable ready означает, что WebSocket снова стал
          // единственным источником durable-событий. До reset/replay
          // останавливаем и дожидаемся уже начатого GET /sync.
          await fenceDurableSource(connectionGeneration)
          this.assertCurrentContext(context)
          if (frame.reset_required) {
            // CC §4.5 п. 3: закрыть соединение и выполнить процедуру §4.8.
            this.setStatus('reset_required')
            this.socket?.suspend()
            try {
              await this.fullResync(context)
            } finally {
              // Ошибка snapshot/list не должна оставлять SyncSocket
              // навсегда suspended. Новое соединение повторит reset,
              // когда backend снова станет доступен.
              if (this.isCurrentContext(context)) this.socket?.resume()
            }
            return
          }

          if (!frame.replay_complete) {
            this.setStatus('replaying')
            return
          }

          // §4.5 п. 5: после replay_complete — кадр visible и повтор очереди.
          this.wsFailures = 0
          await syncStateRepo.patch(context.userId, {
            status: 'live',
            last_ws_connected_at: new Date().toISOString(),
          })
          this.assertCurrentContext(context)
          this.setStatus('live')
          setReplayComplete(true)

          if (!this.isCurrentContext(context)) return
          this.publishCombinedVisibleConversations()
          this.publishCombinedActiveConversation()
          this.requestQueueRun(context)
        },

        onTyping: (frame) => {
          if (!this.isCurrentContext(context)) return
          this.callbacks.onTyping?.(frame)
          for (const listener of this.typingListeners) listener(frame)
        },

        onPresence: (frame) => {
          if (!this.isCurrentContext(context)) return
          this.callbacks.onPresence?.(frame)
          for (const listener of this.presenceListeners) listener(frame)
        },

        onFrameError: async (code, _message, connectionGeneration) => {
          if (!this.isCurrentContext(context)) return
          if (code === 'client_upgrade_required') {
            setReplayComplete(false)
            this.callbacks.onClientUpgradeRequired?.()
            return
          }
          // Нефатально: `not_member` — перезагрузить диалог, соединение не рвём.
          if (code === 'not_member') {
            await fenceDurableSource(connectionGeneration)
            this.assertCurrentContext(context)
            await this.reloadLists(context)
          }
        },

        onBye: (reason) => {
          if (!this.isCurrentContext(context)) return
          this.callbacks.onBye?.(reason)
          if (reason === 'session_revoked') this.callbacks.onSessionLost?.()
          if (reason === 'consent_required') this.callbacks.onConsentRequired?.()
        },

        onSessionInvalid: () => {
          if (!this.isCurrentContext(context)) return
          setReplayComplete(false)
          this.callbacks.onSessionLost?.()
        },

        onForbidden: () => {
          if (!this.isCurrentContext(context)) return
          setReplayComplete(false)
          this.callbacks.onForbidden?.()
        },

        onConnectionFailure: () => this.handleSocketFailure(context),
        onStatusChange: (socketStatus) => this.handleSocketStatus(socketStatus, context),
      },
      () => this.buildHello(context),
    )

    this.socket.start()
  }

  private async buildHello(context: EngineRunContext): Promise<{
    cursor: string | null
    deviceId: string | null
    appVersion: string
    activeConversationId: string | null
    visibleConversations: string[]
  }> {
    this.assertCurrentContext(context)
    const cursor = await syncStateRepo.getCursor(context.userId)
    this.assertCurrentContext(context)
    const me = await meRepo.get(context.userId)
    this.assertCurrentContext(context)

    return {
      cursor,
      deviceId: me?.user.device_id ?? null,
      appVersion: __APP_VERSION__,
      activeConversationId: this.combinedActiveConversation(),
      visibleConversations: this.combinedVisibleConversations(),
    }
  }

  private handleSocketStatus(socketStatus: SocketStatus, context: EngineRunContext): void {
    if (!this.isCurrentContext(context)) return
    // Уже подтверждённый HTTP fallback остаётся durable-источником,
    // пока первый durable WS-кадр не выполнит fence выше.
    if (this.pollingTimer && this.status === 'polling_fallback') return
    if (socketStatus === 'connecting' || socketStatus === 'reconnecting' || socketStatus === 'replaying') {
      setReplayComplete(false)
      this.setStatus('replaying')
      void syncStateRepo
        .patch(context.userId, { status: 'replaying' })
        .then(() => this.assertCurrentContext(context))
        .catch(() => undefined)
    }
  }

  private handleSocketFailure(context: EngineRunContext): void {
    if (!this.isCurrentContext(context)) return
    this.wsFailures += 1
    // `reconnecting` как status дедуплицируется, но счётчик обязан
    // видеть каждую реальную неудачу соединения.
    if (this.wsFailures >= 3) this.startPolling(context)
  }

  /* ===================================================================== */
  /* Polling-fallback                                                       */
  /* ===================================================================== */

  private startPolling(context: EngineRunContext): void {
    if (!this.isCurrentContext(context)) return
    if (this.pollingTimer) return
    // До первого полного успешного poll реплика не подтверждена:
    // UI и outbox остаются offline, пока `has_more=false` не закроет catch-up.
    this.setStatus('offline')
    setReplayComplete(false)
    const pollingGeneration = ++this.pollingGeneration

    const poll = (): void => {
      if (!this.isCurrentContext(context)) return
      if (document.visibilityState !== 'visible') return
      if (this.pollingRequest) return
      const controller = new AbortController()
      this.pollingController = controller
      const signal = combineAbortSignals(context.signal, controller.signal)
      const request = this.pollOnce(context, pollingGeneration, signal).finally(() => {
        // Отмена source также снимает listener с долгоживущего lifecycle signal.
        controller.abort()
        if (this.pollingRequest === request) {
          this.pollingRequest = null
          this.pollingController = null
        }
      })
      this.pollingRequest = request
      void request
    }

    poll()
    this.pollingTimer = setInterval(poll, POLLING_INTERVAL_MS)
  }

  private stopPolling(): void {
    this.pollingGeneration += 1
    this.pollingController?.abort()
    this.pollingController = null
    if (this.pollingTimer) clearInterval(this.pollingTimer)
    this.pollingTimer = null
  }

  /**
   * Перед переходом WebSocket в live ждём уже начатый poll. Его generation
   * guard отменяет транзакцию, а ожидание не даёт двум durable-потокам
   * одновременно применять события.
   */
  private async stopPollingAndWait(): Promise<void> {
    const request = this.pollingRequest
    this.stopPolling()
    if (!request) return
    try {
      await request
    } catch {
      // pollOnce сам классифицирует сетевые/stale ошибки; live-переходу они
      // больше не принадлежат.
    }
  }

  private async pollOnce(
    activeContext?: EngineRunContext,
    activePollingGeneration = this.pollingGeneration,
    pollingSignal?: AbortSignal,
  ): Promise<void> {
    const context = activeContext ?? this.currentContext()
    if (!context) return
    const signal = pollingSignal ?? context.signal
    try {
      if (activePollingGeneration !== this.pollingGeneration) return
      this.assertCurrentContext(context)
      let cursor = await syncStateRepo.getCursor(context.userId)
      if (activePollingGeneration !== this.pollingGeneration) return
      this.assertCurrentContext(context)
      const seenCursors = new Set<string>()
      if (cursor) seenCursors.add(cursor)

      for (let pageIndex = 0; pageIndex < MAX_LIST_PAGES; pageIndex += 1) {
        const result = unwrap(
          await api.GET('/sync', {
            params: { query: { cursor, limit: 500 } as never },
            signal,
          }),
        )
        if (activePollingGeneration !== this.pollingGeneration) return
        this.assertCurrentContext(context)

        for (const event of result.events) {
          if (activePollingGeneration !== this.pollingGeneration) return
          await this.handleEvent(event, context, () => {
            if (activePollingGeneration !== this.pollingGeneration) throw ENGINE_CONTEXT_CHANGED
          })
          if (activePollingGeneration !== this.pollingGeneration) return
          this.assertCurrentContext(context)
        }

        // `next_cursor` продвигается и на пустой terminal page (OpenAPI
        // example). Без этой записи fallback бесконечно перечитывал бы её.
        const nextCursor = result.next_cursor
        if (result.has_more && (!nextCursor || nextCursor === cursor || seenCursors.has(nextCursor))) {
          throw new Error('sync cursor did not progress')
        }
        if (nextCursor && nextCursor !== cursor) {
          await syncStateRepo.patch(context.userId, { cursor: nextCursor })
          if (activePollingGeneration !== this.pollingGeneration) return
          this.assertCurrentContext(context)
          cursor = nextCursor
          seenCursors.add(nextCursor)
        }

        if (result.has_more) continue

        if (activePollingGeneration !== this.pollingGeneration) return
        this.assertCurrentContext(context)
        await syncStateRepo.patch(context.userId, { status: 'polling' })
        if (activePollingGeneration !== this.pollingGeneration) return
        this.assertCurrentContext(context)
        this.setStatus('polling_fallback')
        setReplayComplete(true)
        this.requestQueueRun(context)
        return
      }

      throw new Error('sync page limit exceeded')
    } catch (error) {
      if (activePollingGeneration !== this.pollingGeneration) return
      if (this.isStaleFailure(error, context)) return
      if (error instanceof ApiError && error.code === 'sync_reset_required') {
        try {
          await this.fullResync(context, 'reset', signal)
        } catch (resyncError) {
          if (this.isStaleFailure(resyncError, context)) return
          // Polling запускается в фоне. Ошибка snapshot/lists не должна стать
          // unhandledrejection: следующий тик повторит reset.
        }
        return
      }
      // Сетевой отказ — следующий тик попробует снова.
    }
  }

  /* ===================================================================== */
  /* Применение события                                                     */
  /* ===================================================================== */

  private async handleEvent(
    event: SyncEvent,
    activeContext?: EngineRunContext,
    assertSourceCurrent: () => void = () => undefined,
  ): Promise<void> {
    const context = activeContext ?? this.currentContext()
    if (!context) return
    const assertEventCurrent = (): void => {
      this.assertCurrentContext(context)
      assertSourceCurrent()
    }
    // Source-generation защищает только саму transaction. После того как
    // applyEvent resolved, row/cursor уже committed: WS fence ждёт этот poll,
    // поэтому обязательные effects должны завершиться даже если тем временем
    // durable source переключился с poll на WS.
    const assertEffectsCurrent = (): void => this.assertCurrentContext(context)
    let effects: ApplyEffects
    try {
      assertEventCurrent()
      effects = await applyEvent(event, {
        userId: context.userId,
        assertCurrent: assertEventCurrent,
      })
      assertEffectsCurrent()
    } catch (error) {
      if (this.isStaleFailure(error, context)) return
      // Ошибка применения не должна ронять поток: cursor не сдвинут,
      // событие придёт снова при следующем replay. Ошибку нужно вернуть
      // в SyncSocket/poller, чтобы они остановили текущий batch и не сдвинули cursor следующим событием.
      throw error
    }

    if (effects.sessionRevoked) this.callbacks.onSessionLost?.()
    assertEffectsCurrent()
    if (effects.consentRequired) this.callbacks.onConsentRequired?.()
    assertEffectsCurrent()
    if (effects.matchCreated) this.callbacks.onNewMatch?.(effects.matchCreated)
    assertEffectsCurrent()

    if (effects.deliveredReceipt) {
      // pending receipt уже атомарно сохранён applyEvent вместе с message+cursor.
      this.requestQueueRun(context)
    }

    // §8.5 п. 5: показанные уведомления пары снимаются при block/unmatch.
    if (effects.dismissNotifications?.length) {
      for (const target of effects.dismissNotifications) {
        assertEffectsCurrent()
        void dismissPairNotifications(target).catch(() => undefined)
      }
    }

    // §9.3 п. 7: бинарный кэш service worker инвалидируется по событиям.
    if (effects.invalidateMediaUrls?.length) {
      assertEffectsCurrent()
      navigator.serviceWorker?.controller?.postMessage({
        type: 'MEDIA_INVALIDATE',
        urls: effects.invalidateMediaUrls,
      })
    }

    // Готовое медиа разблокирует сообщение в состоянии `pending_media`.
    if (effects.mediaReady) {
      await this.releasePendingMedia(effects.mediaReady, context)
      assertEffectsCurrent()
      this.emitMedia({ mediaId: effects.mediaReady, state: 'ready' })
    }

    if (effects.mediaRejected) {
      await this.failPendingMedia(effects.mediaRejected, context)
      assertEffectsCurrent()
      this.emitMedia({
        mediaId: effects.mediaRejected,
        state: 'rejected',
        rejectedReason: (event.data as { rejected_reason?: string }).rejected_reason ?? 'other',
      })
    }

    if (effects.refreshConversationIds?.length) {
      await this.refreshPendingConversations(context)
      assertEffectsCurrent()
    }

    assertEffectsCurrent()
    this.callbacks.onEffects?.(effects)
    if (effects.incomingMessage || effects.matchCreated) {
      postTabMessage({
        type: 'sync_effects',
        user_id: context.userId,
        ...(effects.incomingMessage ? { incoming_message: effects.incomingMessage } : {}),
        ...(effects.matchCreated ? { match_created: effects.matchCreated } : {}),
      })
    }
  }

  private async refreshPendingConversations(context: EngineRunContext): Promise<void> {
    const ids = await conversationRefreshRepo.list()
    this.assertCurrentContext(context)
    let retry = false
    for (const conversationId of ids) {
      try {
        const conversation = unwrap(
          await api.GET('/conversations/{conversationId}', {
            params: { path: { conversationId } },
            signal: context.signal,
          }),
        )
        this.assertCurrentContext(context)
        await conversationsRepo.put(conversation)
        this.assertCurrentContext(context)
        await conversationRefreshRepo.remove(conversationId)
        this.assertCurrentContext(context)
      } catch (error) {
        if (this.isStaleFailure(error, context)) throw error
        retry = true
      }
    }

    if (!retry || this.conversationRefreshTimer) return
    this.conversationRefreshTimer = setTimeout(() => {
      this.conversationRefreshTimer = null
      if (!this.isCurrentContext(context)) return
      void this.refreshPendingConversations(context).catch(() => undefined)
    }, LEADERSHIP_RETRY_MS)
  }

  private emitMedia(result: MediaEvent, broadcast = true): void {
    this.recentMedia.set(result.mediaId, { result, at: Date.now() })
    this.pruneRecentMedia()

    if (broadcast && isLeaderTab() && this.sessionUserId) {
      postTabMessage({
        type: 'media_event',
        user_id: this.sessionUserId,
        media_id: result.mediaId,
        state: result.state,
        ...(result.rejectedReason ? { rejected_reason: result.rejectedReason } : {}),
      })
    }

    for (const listener of this.mediaListeners) {
      try {
        listener(result)
      } catch {
        /* подписчик не должен ломать поток событий */
      }
    }
  }

  /** Память результатов живёт минуту — дольше она никому не нужна. */
  private pruneRecentMedia(): void {
    const cutoff = Date.now() - 60_000
    for (const [mediaId, entry] of this.recentMedia) {
      if (entry.at < cutoff) this.recentMedia.delete(mediaId)
    }
  }

  private async releasePendingMedia(mediaId: string, context: EngineRunContext): Promise<void> {
    this.assertCurrentContext(context)
    for (const row of await messagesRepo.waitingForMedia(mediaId)) {
      this.assertCurrentContext(context)
      await messagesRepo.setLocalState(row.conversation_id, row.seq, { local_state: 'queued' })
    }
    this.assertCurrentContext(context)
    this.requestQueueRun(context)
  }

  private async failPendingMedia(mediaId: string, context: EngineRunContext): Promise<void> {
    this.assertCurrentContext(context)
    for (const row of await messagesRepo.waitingForMedia(mediaId)) {
      this.assertCurrentContext(context)
      // ПС §20.9: сообщение не должно выглядеть отправленным.
      await messagesRepo.setLocalState(row.conversation_id, row.seq, {
        local_state: 'failed',
        error_code: 'media_rejected',
      })
    }
    this.assertCurrentContext(context)
  }

  /* ===================================================================== */
  /* Подсказки соединению                                                   */
  /* ===================================================================== */

  setActiveConversation(conversationId: string | null): void {
    this.activeConversationId = conversationId
    this.activeConversationAt = conversationId ? Date.now() : 0
    if (this.isNetworkLeader()) this.publishCombinedActiveConversation()
    else if (this.sessionUserId) {
      postTabMessage({
        type: 'sync_command',
        user_id: this.sessionUserId,
        source_tab_id: TAB_ID,
        command: { kind: 'active_chat', conversation_id: conversationId },
      })
    }
  }

  setVisibleConversations(ids: readonly string[]): void {
    this.visibleConversations = [...ids]
    if (this.isNetworkLeader()) this.publishCombinedVisibleConversations()
    else if (this.sessionUserId) {
      postTabMessage({
        type: 'sync_command',
        user_id: this.sessionUserId,
        source_tab_id: TAB_ID,
        command: { kind: 'visible', conversation_ids: [...ids] },
      })
    }
  }

  sendTyping(conversationId: string, state: 'start' | 'stop'): void {
    if (this.isNetworkLeader()) this.socket?.sendTyping(conversationId, state)
    else if (this.sessionUserId) {
      postTabMessage({
        type: 'sync_command',
        user_id: this.sessionUserId,
        source_tab_id: TAB_ID,
        command: { kind: 'typing', conversation_id: conversationId, state },
      })
    }
  }

  /** Квитанции: по WS, если соединение живо, иначе HTTP через очередь. */
  sendReceipt(conversationId: string, cursors: { delivered_seq?: number; read_seq?: number }): boolean {
    if (this.isNetworkLeader()) return this.socket?.sendReceipt(conversationId, cursors) ?? false
    if (!this.sessionUserId) return false
    postTabMessage({
      type: 'sync_command',
      user_id: this.sessionUserId,
      source_tab_id: TAB_ID,
      command: { kind: 'receipt', conversation_id: conversationId, ...cursors },
    })
    // BroadcastChannel — только ускорение. Без ack квитанция обязана попасть
    // в durable queue через вызывающий useChatActions.markRead.
    return false
  }

  /** Возврат вкладки в foreground — немедленная попытка (CC §4.5 п. 7). */
  reconnectNow(): void {
    this.socket?.reconnectNow()
  }

  /**
   * Браузер сообщил, что сеть вернулась (событие `online`, CC §3.2).
   *
   * `navigator.onLine` — только подсказка; подтверждением служит обмен с
   * сервером. Если соединение живо (heartbeat идёт), оно и есть подтверждение,
   * и состояние нужно объявить заново: `reconnectNow()` в этом случае ничего
   * не делает, и приложение навсегда осталось бы в состоянии «нет сети».
   */
  confirmOnline(): void {
    if (this.socket?.isConnected()) {
      this.callbacks.onStatusChange?.(this.status)
      return
    }

    this.reconnectNow()
  }

  /**
   * SYS-05: фоновые запросы и reconnect приостанавливаются
   * до успешного `POST /me/consents` (CC §4.5, BS §15.1).
   */
  suspendForConsent(): void {
    this.socket?.suspend()
  }

  resumeAfterConsent(): void {
    this.socket?.resume()
  }
}
